import * as R from 'ramda';
import { Endpoints } from '../../../core/api/endpoints';
import { ApiReducer, makeApiReducer, makeApiAction, makeRsaaTypes } from '../../../core/api/rsaa';
import { addError, noLoadingNoError, addLoading, getLoading } from '../../../core/api/module';
import { ActionType } from '../../../core/redux/actionType';


const KEY_ACTION = ActionType.UpdateDevPlanItemDescriptionRequest;
const ACTIONS    = [
  ActionType.UpdateDevPlanItemDescriptionRequest,
  ActionType.UpdateDevPlanItemDescriptionResponse,
  ActionType.UpdateDevPlanItemDescriptionError,
];


type Payload = undefined;
type Meta = {
  itemId: number;
  description: string;
};


/**
 * Dispatch this action to update the description field for a dev plan item
 */
export const updateDevPlanItemDescriptionAction = (
  participantId: number,
  projectId: number,
  itemId: number,
  description: string,
) => makeApiAction<Payload, Meta>({
  method:   'POST',
  endpoint: Endpoints.UpdateDevPlanItemDescription,
  headers:  { 'content-type': 'application/json' },
  body:     JSON.stringify({
    participantId: participantId,
    projectId:     projectId,
    devItemId:     itemId,
    description:   description,
  }),
  types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { itemId, description }),
  bailout:  state => !!getLoading(state, KEY_ACTION, itemId),
});


const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  if (isLoading) {
    return addLoading(state, KEY_ACTION, action.meta.itemId);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, action.meta.itemId);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, action.meta.itemId);

  const itemIndex = successState.devPlan?.items?.findIndex(item => item.devItemId === action.meta.itemId) ?? -1;

  // Can't update an item we didn't find ¯\_(ツ)_/¯
  if (itemIndex === -1) {
    return successState;
  }

  const newItem = R.assoc('description', action.meta.description, successState.devPlan!.items![itemIndex]);

  return R.assocPath(
    ['devPlan', 'items'],
    R.update(itemIndex, newItem, successState.devPlan!.items!),
    successState,
  );
};


export const updateDevPlanItemDescriptionReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
